// lib/fetchSelectOptions.ts

import { OptionKey, OptionMap } from './optionTypes';
import { setCachedOption } from './optionsCache';
import { IBackend } from '@/api/BackendApi';
import { log } from '@/lib/utils/logger';
import { StatusDto } from '@/api/ApiClient';

/**
 * Those are actual select lists, ie. fetched from the /drop-down-choices endpoint
 */
const SELECT_LIST_KEYS: OptionKey[] = [
  'columnType',
  'metadataSource',
  'projectCategory',
  'projectSearchStatus',
  'publicationType',
  'projectStatus',
  'status',
  'visibility',
  'activityCategory',
  'sampleRequestStatus',
  'activityType',
  'reportObject',
  'reportAggregation',
  'reportAttribute',
  'shippingLabelTypes',
] as const;

const isSelectListKey = (key: string) => SELECT_LIST_KEYS.includes(key as any);

/*
 * Those are the keys that are fetched from the backend, but not from the select list endpoint
 * We neeed to map them to the correct backend endpoint & method
 */
function getDedicatedMethod(key: OptionKey, backend: IBackend): () => Promise<any> {
  switch (key) {
    case 'staff':
      return () => backend.user.getStaff(false);
    case 'users':
      return () => backend.user.getUsers(false);
    case 'roles':
      return () => backend.role.getRoles();
    case 'synthesisInstruments':
      return () => backend.synthesisInstrument.list(undefined, false, false);
    case 'synthesisTechniques':
      return () => backend.synthesisTechnique.get(undefined);
    case 'synthesisInstrumentTypes':
      return () => backend.synthesisInstrumentType.list();
    case 'characterizationInstruments':
      return () => backend.characterizationInstrument.list(false);
    case 'characterizationTechniques':
      return () => backend.characterizationTechnique.list();
    case 'characterizationDropdownChoices':
      return () => backend.characterizationInstrument.getDropdownChoices();
    case 'materials':
      return () => backend.material.list('All', false);
    case 'projectMaterials':
      return () => backend.material.list('Project', false);
    case 'sampleMaterials':
      return () => backend.material.list('Sample', false);
    case 'substrates':
      return () => backend.substrate.list();
    case 'substrateDropdownChoices':
      return () => backend.substrate.getDropdownChoices();
    case 'bulkRawMaterials':
      // LEGACY: For recipe lookup columns, should be removed eventually
      return () => backend.bulkRawMaterial.list();
    case 'projects':
      return async () => {
        const all = await backend.project.getAll();
        return all.filter((p) => p.status === StatusDto.Submitted);
      };
    case 'scienceDrivers':
      return () => backend.publication.getScienceDrivers();
    case 'sampleDropdownChoices':
      return () => backend.sample.getDropdownChoices(null, null);
    case 'sampleReportingCategory':
      return () => backend.sample.listReportingCategories(false);
    case 'projectType':
      return () => backend.project.listProjectTypes(undefined);
    case 'instrumentFunding':
      return () => backend.synthesisInstrument.listInstrumentFunding(false);
    case 'shippingTypes':
      return () => backend.shipping.getShippingTypes();
    default:
      throw new Error(`No dedicated method for key: ${key}`);
  }
}

function capitalizeFirstLetter(text: string): string {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1);
}

const inFlightRequests: Partial<Record<OptionKey, Promise<any>>> = {};

export async function fetchSelectOptions<T extends OptionKey>(
  backend: IBackend,
  keys: T[]
): Promise<Pick<OptionMap, T>> {
  const result: Partial<OptionMap> = {};

  const selectListKeys = keys.filter(isSelectListKey);
  const dedicatedKeys = keys.filter((key) => !isSelectListKey(key));

  const uncachedSelectListKeys = selectListKeys.filter((key) => !inFlightRequests[key]);
  if (uncachedSelectListKeys.length > 0) {
    const capitalized = uncachedSelectListKeys.map(capitalizeFirstLetter).join(',');

    const promise = backend.selectList
      .get(capitalized)
      .then((bulk) => {
        for (const key of uncachedSelectListKeys) {
          // Try exact key first, then try capitalized version (e.g., "ShippingLabelTypes")
          const capitalizedKey = capitalizeFirstLetter(key);
          let value = bulk[key] ?? bulk[capitalizedKey];

          if (value === undefined) {
            log.warn(
              `[FetchOptions] Backend did not return option key: ${key} or ${capitalizedKey}. Requested: ${capitalized}. Available keys: ${Object.keys(bulk).join(', ')}`
            );
            // Set to empty array for array types, undefined for others
            const defaultValue = key === 'shippingLabelTypes' ? [] : undefined;
            result[key] = defaultValue as any;
            setCachedOption(key, defaultValue as any);
          } else {
            // shippingLabelTypes is expected to be a string array, but backend returns KeyValueList
            // Convert KeyValueList format [{key: "value", value: "value"}] to string array ["value"]
            if (key === 'shippingLabelTypes' && Array.isArray(value)) {
              value = value.map((item: any) => (typeof item === 'string' ? item : item?.value || item?.key || item));
            }
            result[key] = value as any;
            setCachedOption(key, value as any);
          }
          delete inFlightRequests[key];
        }
      })
      .catch((err) => {
        uncachedSelectListKeys.forEach((key) => delete inFlightRequests[key]);
        // Check for 403 or 500 errors and report them
        if (err && typeof err === 'object' && 'status' in err) {
          const status = err.status as number;
          if (status === 403 || status === 500) {
            log.error(`[FetchOptions] Server error ${status} when fetching select list options: ${capitalized}`, err);
          }
        }
        throw err;
      });

    uncachedSelectListKeys.forEach((key) => {
      inFlightRequests[key] = promise;
    });

    await promise;
  }

  await Promise.all(
    dedicatedKeys.map(async (key) => {
      // Check if the key is already in flight
      if (!inFlightRequests[key]) {
        const promise = getDedicatedMethod(key, backend)()
          .then((data) => {
            log.debug(`[FetchOptions] Fetched and caching ${key}:`, () => ({
              count: Array.isArray(data) ? data.length : 'N/A',
            }));
            result[key] = data;
            setCachedOption(key, data);
            delete inFlightRequests[key];
          })
          .catch((err) => {
            log.error(`[FetchOptions] Failed to fetch ${key}:`, err);
            delete inFlightRequests[key];
            // Check for 403 or 500 errors and report them
            if (err && typeof err === 'object' && 'status' in err) {
              const status = err.status as number;
              if (status === 403 || status === 500) {
                log.error(`[FetchOptions] Server error ${status} when fetching option key: ${key}`, err);
              }
            }
            throw err;
          });
        // Store the promise in the inFlightRequests object => wont be called again until it resolves
        inFlightRequests[key] = promise;
      }
      await inFlightRequests[key];
    })
  ).catch((err) => {
    // Check if this is a 401 authentication error
    if (err && typeof err === 'object' && 'status' in err && err.status === 401) {
      // Re-throw auth errors so they can be handled by the calling hook
      throw err;
    }

    // Check for 403 or 500 errors - these are already logged in the individual catch blocks above
    // but we still need to re-throw them so the hook can handle them
    if (err && typeof err === 'object' && 'status' in err) {
      const status = err.status as number;
      if (status === 403 || status === 500) {
        // These errors are already logged, just re-throw
        throw err;
      }
    }

    log.error('[FetchOptions] fetchSelectOptions failed during dedicated key fetch', err);
    // Re-throw other errors as well so they can be handled properly
    throw err;
  });

  return result as Pick<OptionMap, T>;
}
